import React, { useState, useContext, useEffect, useMemo, FC } from 'react';
import { makeStyles, createStyles, useTheme } from '@material-ui/styles';
import { of, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { useLayoutNodeErrorCount, FormContext } from '..';
import { ExpressionManager } from '../services/ExpressionManager';
import { TableOfContentsCard } from './TableOfContentsCard';
import { Typography, List, ListItem, Divider, Theme } from '@material-ui/core';

import { DFSExpression, MobilePageDefinition } from '../services';
export interface PageDesc {
  index: number;
  // may need to be card def?
  definition: MobilePageDefinition;
  isVisible: boolean;
}
export interface TableOfContentsPageProps {
  desc: PageDesc;
  index: number;
  currentPage: boolean;
  onPageSelected: (page: number) => void;
  onCardSelected: (card: number) => void;
}

var useStyles = makeStyles(function(theme: Theme) {
  return createStyles({
    error: {
      color: theme.palette.error.dark,
      fontWeight: 'bold',
      position: 'absolute',
      right: theme.spacing(2),
      textAlign: 'center',
      verticalAlign: 'middle'
    },
    tocCards: {
      padding: theme.spacing(0, 0, 0, 2),
      backgroundColor: theme.palette.grey[100]
    },
    sidebar: {
      position: 'absolute',
      top: 0,
      left: theme.spacing(0),
      bottom: 0,
      display: 'flex',
      alignItems: 'center'
    },
    sidebarBar: {
      backgroundColor: theme.palette.grey[300],
      height: '100%',
      width: '2px'
    },
    sidebarNotch: {
      verticalAlign: 'middle',
      backgroundColor: theme.palette.grey[300],
      width: theme.spacing(1),
      height: '2px'
    }
  })
});

export const TableOfContentsPage: FC<TableOfContentsPageProps> = React.memo(function(props) {
  const { desc, index, onCardSelected } = props;
  const cards = desc.definition.cards;

  const [ visibleCards, setVisibleCards ] = useState(
    cards
      .filter(c => !c.excludeFromTableOfContents)
      .map((definition, index) => {
        return { definition, index, isVisible: true };
      })
  );

  const [ showSubNavigation, setShowSubnavigation ] = useState(false);

  const expMgr = useContext(FormContext).expMgr as ExpressionManager;

  useEffect(function e() {
    const subscription = combineLatest(
      cards
      .filter(c => !c.excludeFromTableOfContents)
      .map(function(definition, index) {
        let t;

        const visibilityConditions = definition.children.map(function(def) {
          return def.visibility ?? def.visible;
        });
        let implicitVisibility;
        if (visibilityConditions.indexOf(undefined) < 0) {
          implicitVisibility = combineLatest(
            (visibilityConditions as DFSExpression[]).map(function(exp: DFSExpression) {
              return expMgr.watch(exp).pipe(distinctUntilChanged());
            })
          ).pipe(
            map(function(results) {
              return results.indexOf(true);
            }),
            map(function(idx) {
              return idx >= 0;
            })
          )
        } else {
          implicitVisibility = of(true)
        }
        const explicitCondition = (t = definition.visibility) !== null && t !== void 0 ? t : definition.visible;
        const explicitVisibility = explicitCondition !== undefined
          ? expMgr.watch(explicitCondition).pipe(distinctUntilChanged())
          : of(true);
        return combineLatest([explicitVisibility, implicitVisibility])
        .pipe(
          debounceTime(10),
          map(function(e) {
            const [ t, a ] = e;
            return t && a;
          }), map(function(isVisible) {
            return { definition, index, isVisible }
          })
        )
      })).subscribe((newVisibility: any) => {
        setVisibleCards(newVisibility);
      });
    return function() {
      return subscription.unsubscribe();
    }
  }, [expMgr, cards]);

  useEffect(() => {
    if (cards.length === 1 && desc.definition.collapseToc) {
      setShowSubnavigation(false);
    } else {
      setShowSubnavigation(true);
    }
  }, [cards, desc])

  var pagePath = useMemo(function() {
    return '/renderings/eval/pages/' + index;
  }, [index]);
  const errors = useLayoutNodeErrorCount(pagePath, desc.definition);
  const theme: Theme = useTheme();
  const classes = useStyles(props);

  return  <React.Fragment>
            <ListItem
              button = { true }
              onClick = { function() {
                return props.onPageSelected(desc.index)
              } }
              style = { props.currentPage
                ? {
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.grey[100],
                    columnGap: '10px'
                  }
                : { columnGap: '10px' }}>
              <div style={{ display: 'flex' }}>
                <Typography variant = { 'subtitle2' }>
                { desc.definition.title }
                </Typography>
              </div>
              { errors > 0 &&
                <div style={{ display: 'flex', alignItems: 'center' }}><Typography variant = { 'body2' } className = { classes.error }>{desc.definition.collapseToc ? errors : '✽'}</Typography></div>
              }
            </ListItem>
            { props.currentPage && showSubNavigation && 
              <List
                component = { 'nav' }
                className = { classes.tocCards }>
                { visibleCards.map((desc, i) => {
                  return desc.isVisible
                    ? (
                        <TableOfContentsCard
                          key = { i }
                          path = { pagePath + '/cards/' + i }
                          index = { i }
                          definition = { desc.definition }
                          onCardSelected = { onCardSelected }>
                        </TableOfContentsCard>
                      )
                    : null
                  })
                }
              </List>
            }
            <Divider variant = { 'fullWidth' }></Divider>
        </React.Fragment>
}, (previousProps, nextProps) => {
  return  previousProps.currentPage === nextProps.currentPage &&
          previousProps.desc.isVisible === nextProps.desc.isVisible &&
          previousProps.desc.index === nextProps.desc.index;
});
